import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService, { User, LoginCredentials, RegisterData } from '../services/authService';
import { heartbeatService } from '../services/heartbeatService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  isSuperAdmin: () => boolean;
  isTenantAdmin: () => boolean;
  isDriveManager: () => boolean;
  isInterviewer: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Helper function to check if current route is a public route
  const isPublicRoute = (pathname: string): boolean => {
    const publicRoutes = [
      '/',
      '/features',
      '/pricing',
      '/about',
      '/contact',
      '/privacy',
      '/terms',
      '/login',
      '/signup',
      '/forgot-password',
      '/public/checkin',
      '/public/checkin/verify',
      '/public/checkin/review',
      '/public/checkin/token',
      '/admin/lobby-display',
      '/resume-viewer'
    ];
    
    return publicRoutes.some(route => pathname.startsWith(route));
  };

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const storedUser = authService.getStoredUser();
          if (storedUser) {
            setUser(storedUser);
          } else {
            // Try to get fresh user data from API
            const freshUser = await authService.getCurrentUser();
            setUser(freshUser);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear invalid auth data
        authService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Manage heartbeat service based on authentication status and route
  useEffect(() => {
    const currentPath = location.pathname;
    
    if (user && !isPublicRoute(currentPath)) {
      // User is logged in and on authenticated route - start heartbeat
      console.log('🔄 Starting heartbeat service for authenticated route:', currentPath);
      heartbeatService.startHeartbeat();
    } else {
      // User is logged out or on public route - stop heartbeat
      console.log('🔄 Stopping heartbeat service - user:', !!user, 'route:', currentPath, 'isPublic:', isPublicRoute(currentPath));
      heartbeatService.stopHeartbeat();
    }

    // Cleanup on unmount
    return () => {
      heartbeatService.stopHeartbeat();
    };
  }, [user, location.pathname]);

  // Handle navigation after user state changes
  useEffect(() => {
    if (user && !isLoading && shouldNavigate) {
      console.log('Navigating user with role:', user.role);
      // Navigate based on user role
      switch (user.role) {
        case 'super_admin':
          console.log('Navigating to super admin dashboard');
          navigate('/super-admin/dashboard');
          break;
        case 'tenant_admin':
        case 'drive_manager':
        case 'interviewer':
          console.log('Navigating to admin dashboard');
          navigate('/admin/dashboard');
          break;
        default:
          console.log('Navigating to default admin dashboard');
          navigate('/admin/dashboard');
      }
      setShouldNavigate(false); // Reset the flag
    }
  }, [user, isLoading, shouldNavigate, navigate]);

  const login = async (credentials: LoginCredentials) => {
    try {
      setIsLoading(true);
      const response = await authService.login(credentials);
      setUser(response.user);
      
      // Show welcome page for admin users (not super admin)
      if (response.user.role !== 'super_admin') {
        navigate('/welcome');
      } else {
        setShouldNavigate(true); // Trigger navigation for super admin
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    company_name: string;
    plan: 'starter' | 'professional' | 'enterprise';
  }) => {
    try {
      setIsLoading(true);
      const response = await authService.register(userData);
      
      // Store user data
      setUser(response.user);
      // setToken(response.token); // This line was removed as per the new_code
      
      // Store in localStorage
      // localStorage.setItem('authToken', response.token); // This line was removed as per the new_code
      // localStorage.setItem('user', JSON.stringify(response.user)); // This line was removed as per the new_code
      
      // toast.success('Registration successful! Welcome to Quacklabs Drive Management.'); // This line was removed as per the new_code
      
      // Navigate to setup animation page
      navigate('/setup');
    } catch (error: any) {
      console.error('Registration error:', error);
      // toast.error(error.response?.data?.message || 'Registration failed. Please try again.'); // This line was removed as per the new_code
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if API call fails
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      if (authService.isAuthenticated()) {
        const freshUser = await authService.getCurrentUser();
        setUser(freshUser);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      // If refresh fails, logout user
      await logout();
    }
  };

  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return user ? roles.includes(user.role) : false;
  };

  const isSuperAdmin = (): boolean => {
    return hasRole('super_admin');
  };

  const isTenantAdmin = (): boolean => {
    return hasRole('tenant_admin');
  };

  const isDriveManager = (): boolean => {
    return hasRole('drive_manager');
  };

  const isInterviewer = (): boolean => {
    return hasRole('interviewer');
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
    hasRole,
    hasAnyRole,
    isSuperAdmin,
    isTenantAdmin,
    isDriveManager,
    isInterviewer,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
